import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Grid, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { useEffect, useMemo } from 'react'
import { AppDispatch } from '../../store'
import {
  DropletOutlinedIcon,
  PeopleIcon,
  ShieldOutlinedIcon,
  WifiIcon,
} from '../../components/icons'
import CheckIcon from '@mui/icons-material/Check'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { LocationIcon } from '../../components/icons/LocationIcon'
import { NoteIcon } from '../../components/icons/NoteIcon'
import {
  PubWaterSystemActions,
  PubWaterSystemSelector,
} from '../../store/publicWaterSystem/pubWaterSystem'
import { fNumber } from '../../utils/formatNumber'
import { ViolationEnforcementActions } from '../../store/ViolationEnforcements'
import { ViolationEnforcementSelector } from '../../store/ViolationEnforcements/violationEnforcement.selector'
import { ownerTypeCodeToLong, primSrcCdToLong } from '../../utils'


export const UtilityInfoPage = () => {
  const theme = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const summary = useSelector(PubWaterSystemSelector.makeObjectSummary)
  const years = 5
  const violCategories = useSelector(state=>
    ViolationEnforcementSelector.groupedEnfByViolation.makeViolationCategoryCountFromGroupedEnf(state,years),
  )
  const violations = useSelector(ViolationEnforcementSelector.groupedEnfByViolation.data)
  const eventMilestones = useSelector(PubWaterSystemSelector.eventMilestones.data)
  const { name, address, pop_served, svc_connections, prim_source, owner_type, pwsId } = summary

  const recentViolations = useMemo(() =>
    violations.filter(item => moment(item.VIOL_FIRST_REPORTED_DATE) > moment().subtract(10, 'years')),
    [violations]
  )
  const resolvedRecentCount = useMemo(() =>
    recentViolations.filter(item =>
      item.VIOLATIONS?.some(v => v.VIOLATION_STATUS === 'Resolved' || v.VIOLATION_STATUS === 'Addressed')
    ).length,
    [recentViolations]
  )
  const olderViolationsCount = violations.length - recentViolations.length
  const earliestYear = useMemo(() => {
    if (violations.length === 0) return ''
    const dates = violations.map(v => moment(v.VIOL_FIRST_REPORTED_DATE)).filter(d => d.isValid())
    return dates.length > 0 ? moment.min(dates).format('YYYY') : ''
  }, [violations])
  const fetchViolationSummary = async (pwsId) => {
    await dispatch(
      ViolationEnforcementActions.fetchViolationsEnforcementsGroupedByViolationAsync({
        pwsId,
        options: { params: { $paging: 'false' } },
      }),
    )
    await dispatch(
      PubWaterSystemActions.fetchPWSEventMilestonesWDescAsync({pwsId, options:{params: {$paging: false}}})
    )
  }

  useEffect(() => {
    if (pwsId) {
      fetchViolationSummary(pwsId)
    }
  }, [pwsId])
  return (
    <Grid container spacing={2} direction="column" sx={{ pt: 2 }}>
      <Typography variant="h4">Your water utility</Typography>
      <Typography>{name}</Typography>

      <Grid
        container
        direction="column"
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: theme.palette.grey[100],
          borderRadius: theme.shape.borderRadius,
        }}
      >
        <Grid container sx={{ width: '100%' }}>
          <Grid container direction="column" wrap="nowrap">
            <NoteIcon />
            <Typography variant="h4">{name}</Typography>
          </Grid>
        </Grid>
        <Grid container direction="row" spacing={1}>
          <Grid container direction="column" size={{ xs: 12, md: 6 }}>
            <LocationIcon color="primary" />
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
              Address
            </Typography>
            <Typography variant="h5">{address}</Typography>
          </Grid>
          <Grid container direction="column" size={{ xs: 12, sm: 4, md: 3 }}>
            <PeopleIcon color="primary" />
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
              Population Served
            </Typography>
            <Typography variant="h5">{fNumber(pop_served || 0)}</Typography>
          </Grid>
          <Grid container direction="column" size={{ xs: 12, sm: 4, md: 3 }}>
            <WifiIcon color="primary" />
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
              Service Connections
            </Typography>
            <Typography variant="h5">{fNumber(svc_connections || 0)}</Typography>
          </Grid>
          <Grid container direction="column" size={{ xs: 12, sm: 4, md: 6 }} />
          <Grid container direction="column" size={{ xs: 12, sm: 4, md: 3 }}>
            <DropletOutlinedIcon color="primary" />
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
              Source Type
            </Typography>
            <Typography variant="h5">{primSrcCdToLong(prim_source || '')}</Typography>
          </Grid>
          <Grid container direction="column" size={{ xs: 12, sm: 4, md: 3 }}>
            <ShieldOutlinedIcon color="primary" />
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
              Ownership
            </Typography>
            <Typography variant="h5">{ownerTypeCodeToLong(owner_type || '')}</Typography>
          </Grid>
        </Grid>
      </Grid>
      <Typography variant="h4">Violations</Typography>

      <Accordion
        defaultExpanded
        elevation={0}
        disableGutters
        sx={{
          border: '1px solid',
          borderColor: theme.palette.grey[100],
          borderRadius: `${theme.shape.borderRadius}px !important`,
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        }}
      >
        <AccordionSummary expandIcon={<KeyboardArrowDownIcon />} sx={{ px: 2, py: 1 }}>
          <Box>
            <Typography variant="h4">All time</Typography>
            <Typography variant="h3" sx={{ color: theme.palette.primary.main }}>{violations.length}</Typography>
            <Typography variant="body1">Violations</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
          <Stack spacing={2}>
            {/* Past 10 years */}
            <Accordion
              defaultExpanded
              elevation={0}
              disableGutters
              sx={{
                border: '1px solid',
                borderColor: theme.palette.grey[100],
                borderRadius: `${theme.shape.borderRadius}px !important`,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: 0 },
              }}
            >
              <AccordionSummary expandIcon={<KeyboardArrowDownIcon />} sx={{ px: 2, py: 1 }}>
                <Box>
                  <Typography variant="h4">Past 10 years</Typography>
                  <Typography>
                    <Typography component="span" variant="h3" sx={{ color: theme.palette.primary.main }}>{recentViolations.length}</Typography>
                    {' '}
                    <Typography component="span" variant="body1">violations</Typography>
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h5" sx={{ mb: 0.5 }}>Recent Violations (Past 10 Years)</Typography>
                    <Typography variant="body2" color="text.secondary">
                      This utility has had {recentViolations.length} regulatory violation{recentViolations.length !== 1 ? 's' : ''} in the past 10 years.
                      {resolvedRecentCount === recentViolations.length && recentViolations.length > 0
                        ? ' All recent violations have been resolved.'
                        : resolvedRecentCount > 0
                        ? ` ${resolvedRecentCount} of ${recentViolations.length} violations have been resolved.`
                        : ''}
                      {olderViolationsCount > 0 && earliestYear
                        ? ` ${olderViolationsCount} additional violation${olderViolationsCount !== 1 ? 's' : ''} occurred between ${earliestYear} and ${moment().subtract(10, 'years').format('YYYY')}.`
                        : ''}
                    </Typography>
                  </Box>
                  {recentViolations.map((item) => {

                    const violation = item.VIOLATIONS?.[0]
                    const status = item?.VIOLATION_STATUS
                    const isResolved = status === 'Resolved' || status === 'Addressed'
                    const lastReportedDate = violation?.VIOL_LAST_REPORTED_DATE || item.VIOL_LAST_REPORTED_DATE
                    return (
                      <Box
                        key={item.VIOLATION_ID}
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: theme.palette.grey[100],
                          borderRadius: `${(theme.shape.borderRadius as number) / 2}px`,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1 }}>
                            <Box
                              sx={{
                                backgroundColor: isResolved
                                  ? theme.palette.success.bgcolor
                                  : theme.palette.error.bgcolor,
                                borderRadius: '8px',
                                p: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {isResolved
                                ? <CheckIcon color="success" sx={{ fontSize: 24 }} />
                                : <ErrorOutlineIcon color="error" sx={{ fontSize: 24 }} />
                              }
                            </Box>
                            <Box>
                              <Typography variant="h6" fontWeight={600}>
                                {violation?.VIOLATION_DESCRIPTION || item.CONTAMINANTS?.[0]?.CONTAMINANT_DESCRIPTION || 'Violation'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {item.CONTAMINANTS?.map(c => c.CONTAMINANT_DESCRIPTION).join(', ')}
                              </Typography>

                              {status && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isResolved
                                      ? theme.palette.success.main
                                      : status === 'Unaddressed'
                                      ? theme.palette.error.main
                                      : theme.palette.text.secondary,
                                    fontWeight: 500,
                                    mt: 0.5,
                                  }}
                                >
                                  {status}{lastReportedDate ? `: ${moment(lastReportedDate).format('MMMM YYYY')}` : ''}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, ml: 2 }}>
                            {moment(item.VIOL_FIRST_REPORTED_DATE).format('MMMM YYYY')}
                          </Typography>
                        </Stack>
                      </Box>
                    )
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Since earliest year */}
            {earliestYear && (
              <Accordion
                elevation={0}
                disableGutters
                sx={{
                  border: '1px solid',
                  borderColor: theme.palette.grey[100],
                  borderRadius: `${theme.shape.borderRadius}px !important`,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { margin: 0 },
                }}
              >
                <AccordionSummary expandIcon={<KeyboardArrowDownIcon />} sx={{ px: 2, py: 1 }}>
                  <Box>
                    <Typography variant="h4">Since {earliestYear}</Typography>
                    <Typography variant="h3" sx={{ color: theme.palette.primary.main }}>{violations.length}</Typography>
                    <Typography variant="body1">Violations</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                  <Stack spacing={1}>
                    {[...violations].sort((a, b) => moment(b.VIOL_FIRST_REPORTED_DATE).valueOf() - moment(a.VIOL_FIRST_REPORTED_DATE).valueOf()).map((item) => {
                      const violation = item.VIOLATIONS?.[0]
                      const status = item?.VIOLATION_STATUS
                      const isResolved = status === 'Resolved' || status === 'Addressed'
                      const lastReportedDate = violation?.VIOL_LAST_REPORTED_DATE || item.VIOL_LAST_REPORTED_DATE
                      return (
                        <Box
                          key={item.VIOLATION_ID}
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: theme.palette.grey[100],
                            borderRadius: `${(theme.shape.borderRadius as number) / 2}px`,
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  backgroundColor: isResolved
                                    ? theme.palette.success.bgcolor
                                    : theme.palette.error.bgcolor,
                                  borderRadius: '8px',
                                  p: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                {isResolved
                                  ? <CheckIcon color="success" sx={{ fontSize: 24 }} />
                                  : <ErrorOutlineIcon color="error" sx={{ fontSize: 24 }} />
                                }
                              </Box>
                              <Box>
                                <Typography variant="h6" fontWeight={600}>
                                  {violation?.VIOLATION_DESCRIPTION || item.CONTAMINANTS?.[0]?.CONTAMINANT_DESCRIPTION || 'Violation'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {item.CONTAMINANTS?.map(c => c.CONTAMINANT_DESCRIPTION).join(', ')}
                                </Typography>
                                {status && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: isResolved
                                        ? theme.palette.success.main
                                        : status === 'Unaddressed'
                                        ? theme.palette.error.main
                                        : theme.palette.text.secondary,
                                      fontWeight: 500,
                                      mt: 0.5,
                                    }}
                                  >
                                    {status}{lastReportedDate ? `: ${moment(lastReportedDate).format('MMMM YYYY')}` : ''}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, ml: 2 }}>
                              {moment(item.VIOL_FIRST_REPORTED_DATE).format('MMMM YYYY')}
                            </Typography>
                          </Stack>
                        </Box>
                      )
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Violations Past 5 Years */}
            <Accordion
              elevation={0}
              disableGutters
              sx={{
                border: '1px solid',
                borderColor: theme.palette.grey[100],
                borderRadius: `${theme.shape.borderRadius}px !important`,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: 0 },
              }}
            >
              <AccordionSummary expandIcon={<KeyboardArrowDownIcon />} sx={{ px: 2, py: 1 }}>
                <Box>
                  <Typography variant="h4">Violations (Past {years} Years)</Typography>
                  <Typography>
                    <Typography component="span" variant="h3" sx={{ color: theme.palette.primary.main }}>
                      {violations.filter(item => moment(item.VIOL_LAST_REPORTED_DATE) > moment().subtract(years, 'years')).length}
                    </Typography>
                    {' '}
                    <Typography component="span" variant="body1">violations</Typography>
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flexStart"
                  alignItems="center"
                  width="100%"
                  sx={{ mb: 2 }}
                >
                  {violCategories && violCategories.length === 0 && (
                    <Chip variant="outlined-success" label={`No violations recorded within the last ${years} years`}/>
                  )}
                  {violCategories && violCategories.length > 0 && violCategories.map((item) => (
                    <Tooltip title={item.description} key={item.category_code}>
                      <Chip
                        variant="filled-warning-light"
                        label={`${item.category_code}:${item.count}`}
                      />
                    </Tooltip>
                  ))}
                </Stack>
                {violations
                  .filter(item => moment(item.VIOL_LAST_REPORTED_DATE) > moment().subtract(years, 'years'))
                  .map((item) => {
                    const violation = item.VIOLATIONS?.[0]
                    const status = violation?.VIOLATION_STATUS
                    const isResolved = status === 'Resolved' || status === 'Addressed'
                    const lastReportedDate = violation?.VIOL_LAST_REPORTED_DATE || item.VIOL_LAST_REPORTED_DATE
                    return (
                      <Box
                        key={item.VIOLATION_ID}
                        sx={{
                          p: 2,
                          mb: 1,
                          border: '1px solid',
                          borderColor: theme.palette.grey[100],
                          borderRadius: `${(theme.shape.borderRadius as number) / 2}px`,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1 }}>
                            <Box
                              sx={{
                                backgroundColor: isResolved
                                  ? theme.palette.success.bgcolor
                                  : theme.palette.error.bgcolor,
                                borderRadius: '8px',
                                p: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {isResolved
                                ? <CheckIcon color="success" sx={{ fontSize: 24 }} />
                                : <ErrorOutlineIcon color="error" sx={{ fontSize: 24 }} />
                              }
                            </Box>
                            <Box>
                              <Typography variant="h6" fontWeight={600}>
                                {violation?.VIOLATION_DESCRIPTION || item.CONTAMINANTS?.[0]?.CONTAMINANT_DESCRIPTION || 'Violation'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {item.CONTAMINANTS?.map(c => c.CONTAMINANT_DESCRIPTION).join(', ')}
                              </Typography>

                              {status && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isResolved
                                      ? theme.palette.success.main
                                      : status === 'Unaddressed'
                                      ? theme.palette.error.main
                                      : theme.palette.text.secondary,
                                    fontWeight: 500,
                                    mt: 0.5,
                                  }}
                                >
                                  {status}{lastReportedDate ? `: ${moment(lastReportedDate).format('MMMM YYYY')}` : ''}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, ml: 2 }}>
                            {moment(item.VIOL_LAST_REPORTED_DATE).format('MMMM YYYY')}
                          </Typography>
                        </Stack>
                      </Box>
                    )
                  })}
              </AccordionDetails>
            </Accordion>
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Grid
        container
        direction="column"
        justifyContent="flex-start"
        flexDirection="row"
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: theme.palette.grey[100],
          borderRadius: theme.shape.borderRadius,
        }}
      >
        <Typography variant="h4" sx={{flexBasis:"100%"}}>Major Events and Milestones</Typography>
        <Chip variant="filled-neutral-light" label={`${eventMilestones.length} recorded event`}  />
      </Grid>
      <Grid
        container
        direction="column"
        alignItems="center"
        sx={{
          p: 2,
          background: theme.palette.primary.dark,
          borderRadius: theme.shape.borderRadius,
        }}
      >
        <Typography variant="h4" color="white">
          See how your results align with utility monitoring
        </Typography>
        <Typography variant="body1" color="white">
          Get personalized recommendations based on your utility's specific water quality profile
        </Typography>
        <Button color="primary" variant="contained" size="large" onClick={()=>navigate('/profile/recommendations')}>
          View Recommendations
        </Button>
      </Grid>
    </Grid>
  )
}
